import { z } from "zod";

import { AUDIT_CATEGORIES, MEMBER_PROJECT_ACCESS, PROJECT_CREATION } from "../admin-types";
import { ORG_ROLES, PROJECT_ROLES, TEAM_SIZES } from "../types";

/**
 * Everything the mock identity service remembers, validated with zod on every load. Stored in this browser's
 * localStorage under a versioned key; a schema change bumps the version and starts from the seed again.
 * Codes, invitation tokens and callback codes are stored only as SHA-256 hashes (FR-ADMIN-032).
 */
export const IDENTITY_STORAGE_KEY = "orthos.identity.v1";

const role = z.enum(ORG_ROLES);
const authMethod = z.enum(["email_code", "passkey", "google", "microsoft"]);

const user = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  initials: z.string(),
  colour: z.number().int(),
  title: z.string(),
  createdAt: z.number(),
  termsVersion: z.string().optional(),
  termsAcceptedAt: z.number().optional(),
  ageConfirmedAt: z.number().optional(),
  workspaceChosen: z.boolean(),
  timeZone: z.string(),
  units: z.enum(["metric", "imperial"]),
  coa: z.object({ number: z.string(), validUntil: z.string().optional() }).optional(),
  /** Passkeys enrolled in AuthKit; the mock treats them all as held by this device. */
  passkeys: z.array(z.object({ id: z.string(), label: z.string(), createdAt: z.number() })),
  /** Two-step method enrolled in AuthKit, e.g. "Authenticator app". */
  twoStep: z.string().optional(),
  passkeyPromptDismissed: z.boolean(),
});

const allowances = z.object({
  seats: z.number().int(),
  guests: z.number().int(),
  storageGb: z.number().int(),
});

/** FR-ADMIN-009: name, logo, country, state, region fixed at creation, status and allowances. No money field. */
const organisation = z.object({
  id: z.string(),
  workosId: z.string(),
  name: z.string(),
  projectPrefix: z.string(),
  country: z.string(),
  state: z.string(),
  city: z.string(),
  region: z.string(),
  status: z.enum(["active", "read_only", "suspended", "closing"]),
  allowances,
  storageUsedGb: z.number(),
  accessFrom: z.string(),
  accessUntil: z.string(),
  setupComplete: z.boolean(),
  /** Plan (D-034): "studio" activates the organisation, null means not activated. Absent in older saved state: studio. */
  plan: z.enum(["studio"]).nullable().optional(),
  twoStepRequired: z.boolean(),
  createdAt: z.number(),
  /** Admin console policies (FR-ADMIN-012, 047, 111); absent means the defaults. */
  policies: z
    .object({
      memberProjectAccess: z.enum(MEMBER_PROJECT_ACCESS),
      projectCreation: z.enum(PROJECT_CREATION),
      sessionMaxHours: z.number().int().optional(),
    })
    .optional(),
});

const membership = z.object({
  userId: z.string(),
  organisationId: z.string(),
  role,
  joinedAt: z.number(),
  /** Suspended: keeps the seat, cannot sign in (FR-ADMIN-118). */
  suspended: z.boolean().optional(),
});

const invitation = z.object({
  id: z.string(),
  tokenHash: z.string(),
  organisationId: z.string(),
  email: z.string(),
  role,
  invitedBy: z.string(),
  createdAt: z.number(),
  expiresAt: z.number(),
  lastSentAt: z.number(),
  status: z.enum(["pending", "accepted", "declined", "revoked"]),
});

const orgRequest = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  firm: z.string(),
  city: z.string(),
  teamSize: z.enum(TEAM_SIZES),
  disciplines: z.array(z.enum(["A", "I", "L", "U", "H", "E"])),
  message: z.string(),
  status: z.enum(["pending", "approved", "declined"]),
  createdAt: z.number(),
  decidedAt: z.number().optional(),
  decisionMessage: z.string().optional(),
  organisationId: z.string().optional(),
});

const projectRequest = z.object({
  id: z.string(),
  organisationId: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  userId: z.string(),
  message: z.string(),
  status: z.enum(["pending", "approved", "declined"]),
  createdAt: z.number(),
  expiresAt: z.number(),
  role: z.enum(PROJECT_ROLES).optional(),
});

const projectGrant = z.object({
  organisationId: z.string(),
  projectId: z.string(),
  userId: z.string(),
  role: z.enum(PROJECT_ROLES),
});

const session = z.object({
  id: z.string(),
  userId: z.string(),
  organisationId: z.string().optional(),
  createdAt: z.number(),
  lastActiveAt: z.number(),
  authMethod,
  device: z.string(),
  browser: z.string(),
  city: z.string(),
  ip: z.string(),
  endedAt: z.number().optional(),
  endReason: z.enum(["signed_out", "revoked", "expired"]).optional(),
});

/** A hosted sign-in started by startSignIn; `state` round-trips through AuthKit. */
const signIn = z.object({
  state: z.string(),
  screen: z.enum(["sign-in", "sign-up"]),
  returnTo: z.string(),
  invitationId: z.string().optional(),
  expiresAt: z.number(),
});

const magicCode = z.object({
  state: z.string(),
  email: z.string(),
  codeHash: z.string(),
  issuedAt: z.number(),
  expiresAt: z.number(),
  attempts: z.number().int(),
  lockedUntil: z.number().optional(),
  used: z.boolean(),
});

/** Authenticated in AuthKit, waiting on the passkey prompt or the callback. */
const authenticated = z.object({
  state: z.string(),
  userId: z.string(),
  method: authMethod,
  callbackCodeHash: z.string().optional(),
  expiresAt: z.number(),
});

const email = z.object({
  id: z.string(),
  to: z.string(),
  subject: z.string(),
  sentAt: z.number(),
  body: z.array(z.string()),
  href: z.string(),
  linkLabel: z.string(),
});

/** One admin action (FR-ADMIN-060). */
const auditRecord = z.object({
  id: z.string(),
  organisationId: z.string(),
  occurredAt: z.number(),
  actorId: z.string(),
  actorName: z.string(),
  category: z.enum(AUDIT_CATEGORIES),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  targetName: z.string(),
  detail: z.record(z.string(), z.unknown()),
});

export const identityStateSchema = z.object({
  users: z.array(user),
  organisations: z.array(organisation),
  memberships: z.array(membership),
  invitations: z.array(invitation),
  orgRequests: z.array(orgRequest),
  projectRequests: z.array(projectRequest),
  projectGrants: z.array(projectGrant),
  sessions: z.array(session),
  /** This browser's session id: the stand-in for the identity service's HttpOnly cookie. */
  cookie: z.string().optional(),
  signIns: z.array(signIn),
  magicCodes: z.array(magicCode),
  /** Code send times per email, for the 10-per-hour limit (FR-ADMIN-003). */
  codeSends: z.record(z.string(), z.array(z.number())),
  authenticated: z.array(authenticated),
  /** Emails the mock would have sent (WorkOS invitation emails), for the demo inbox. */
  outbox: z.array(email),
  /** Failed share-link passwords per link (FR-COLLAB-103). */
  shareFailures: z.record(z.string(), z.array(z.number())),
  /** The admin audit log, newest last. */
  audit: z.array(auditRecord).optional(),
});

export type IdentityState = z.infer<typeof identityStateSchema>;
export type StoredUser = IdentityState["users"][number];
export type StoredOrganisation = IdentityState["organisations"][number];
export type StoredInvitation = IdentityState["invitations"][number];
export type StoredSession = IdentityState["sessions"][number];
export type StoredOrgRequest = IdentityState["orgRequests"][number];
export type StoredProjectRequest = IdentityState["projectRequests"][number];
export type StoredAuditRecord = NonNullable<IdentityState["audit"]>[number];
